"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.extractAppParams = exports.calculateTotalPrice = void 0;
exports.formatMicrounits = formatMicrounits;
Object.defineProperty(exports, "getActiveBundleId", {
  enumerable: true,
  get: function () {
    return _domainUtils.getActiveBundleId;
  }
});
exports.getProductTerm = exports.getProductQuantity = exports.getPriceDisplay = exports.getPriceDescription = exports.getAppParams = exports.getAllProductsWithSameItem = void 0;
exports.getRenewalDetails = getRenewalDetails;
exports.getVisitorCookie = exports.getTermTypeText = exports.getTermPriceText = exports.getSelectedOptionValue = exports.getSavingsTag = void 0;
Object.defineProperty(exports, "hasDomainsInFlow", {
  enumerable: true,
  get: function () {
    return _domainUtils.hasDomainsInFlow;
  }
});
Object.defineProperty(exports, "isBundleProduct", {
  enumerable: true,
  get: function () {
    return _domainUtils.isBundleProduct;
  }
});
Object.defineProperty(exports, "isProductDomain", {
  enumerable: true,
  get: function () {
    return _domainUtils.isProductDomain;
  }
});
exports.updateSelection = exports.updateQuantity = exports.updateInstanceTerm = exports.updateCartWithInstance = exports.parseInstancePath = void 0;
var _intlFormatter = require("@godaddy/intl-formatter");
var _fetchWithHeader = require("./fetchWithHeader");
var _appConfig = require("../config/app-config");
var _base = require("@site/pattern-library-utils/base.functions");
var _domainUtils = require("./domain-utils");
/* eslint-disable max-params, max-len */

/**
 * Parses an instance path string into its components
 *
 * @param {string} path The path string to parse (format: 'product/displayArea/itemName')
 * @returns {Array<string>} Array containing [product, displayArea, itemName]
 */
const parseInstancePath = path => {
  if (typeof path !== 'string') {
    return [null, null, null];
  }
  return path.split('/');
};

/**
 * Gets all products that have the same itemName in the given displayArea
 *
 * @param {Object} instance The instance object to search
 * @param {string} itemName The item name to search for
 * @param {string} displayArea The display area to search in
 * @returns {Array<string>} Array of product keys that have the specified item
 */
exports.parseInstancePath = parseInstancePath;
const getAllProductsWithSameItem = (instance, itemName, displayArea) => {
  if (!instance) {
    return [];
  }
  return Object.keys(instance).filter(productKey => {
    return Boolean(instance[productKey]?.[displayArea]?.[itemName]);
  });
};

/**
 * Formats the given price value in microunits
 *
 * @param {number} priceValue The price value to be formatted
 * @param {string} market The market for which the price is being formatted
 * @param {string} currency The currency in which the price is being formatted
 * @returns {string} The formatted price value.
 */
exports.getAllProductsWithSameItem = getAllProductsWithSameItem;
function formatMicrounits(priceValue, market, currency) {
  const currencyFormatter = new _intlFormatter.Currency();
  return currencyFormatter.formatMicrounits(priceValue, market, currency);
}

/**
 * Returns the term type text based on the term type, content, and number of terms
 *
 * @param {string} termType The type of term (e.g., 'MONTH', 'YEAR')
 * @param {object} content The content object containing month and year texts
 * @param {number} numberOfTerms The number of terms
 * @returns {string} The term type text
 */
const getTermTypeText = (termType, content, numberOfTerms) => {
  let termTypeText;
  switch (termType) {
    case 'MONTH':
      termTypeText = numberOfTerms > 1 ? content.monthPlural : content.monthSingular;
      break;
    case 'YEAR':
      termTypeText = numberOfTerms > 1 ? content.yearPlural : content.yearSingular;
      break;
    default:
      termTypeText = '';
      break;
  }
  return termTypeText;
};

/**
 * Returns the price text based on the term type
 *
 * @param {string} termType Term type ('MONTH' or 'YEAR')
 * @param {object} content Content object containing price information
 * @param {string} perMonthOverride Override for the per month (/mo) price content
 * @returns {string} Price text based on the term type
 */
exports.getTermTypeText = getTermTypeText;
const getTermPriceText = (termType, content, perMonthOverride) => {
  let termPriceText;
  switch (termType) {
    case 'MONTH':
      termPriceText = perMonthOverride || content.perMonth;
      break;
    case 'YEAR':
      // Can be adjusted to use perYearOverride if needed
      termPriceText = content.perYear;
      break;
    default:
      termPriceText = '';
      break;
  }
  return termPriceText;
};

/**
 * Checks if the selected option value exists in the given instance object
 *
 * @param {object} instance The instance object to check
 * @param {string} path The instancePath to the selected option value in the instance object
 * @returns {boolean} Returns true if the selected option value not "none", false otherwise
 */
exports.getTermPriceText = getTermPriceText;
const getSelectedOptionValue = (instance, path) => {
  if (typeof path !== 'string') {
    return false;
  }
  const [product, displayArea, itemName] = parseInstancePath(path);
  const productData = instance?.[product] || {};
  const optionData = productData?.[displayArea] || {};
  const itemData = optionData?.[itemName] || {};

  // If itemName is not provided, is because it's a top level product
  if (!itemName) {
    return productData?.properties?.selection;
  }
  return itemData?.selection;
};

/**
 * Updates the value of a nested property in an instance object
 *
 * @param {object} instance The instance object to update
 * @param {string} path The path to the nested property in dot notation
 * @param {string} value The new value to set for the nested property
 * @param {object} data The data object containing product definitions (optional)
 */
exports.getSelectedOptionValue = getSelectedOptionValue;
const updateInstanceTerm = (instance, path, value, data) => {
  const [product] = parseInstancePath(path);
  instance[product].properties.term.numberOfTerms = value;

  // Sync addon terms to match parent product term
  // Only update addons that don't have fixedNumberOfTerms
  if (instance[product].addons && data?.[product]) {
    Object.keys(instance[product].addons).forEach(addonKey => {
      const addon = instance[product].addons[addonKey];
      const addonData = data[product].addons?.[addonKey];

      // Only update if addon has a term and doesn't have fixedNumberOfTerms
      if (addon.term && !addonData?.attributes?.fixedNumberOfTerms) {
        addon.term.numberOfTerms = value;
      }
    });
  }
};

/* eslint-disable complexity */
/**
 * Updates the selection in the instance object
 *
 * @param {object} instance The instance object to update
 * @param {string|Array<string>} path The path to the selection - either a string in format 'product/displayArea/itemName' or an array of path strings to process
 * @param {string} value The new value to set for the selection
 */
exports.updateInstanceTerm = updateInstanceTerm;
const updateSelection = (instance, path, value) => {
  // Handle array of paths
  if (Array.isArray(path)) {
    path.forEach(singlePath => {
      updateSelection(instance, singlePath, value);
    });
    return;
  }

  // Handle single path
  const [product, displayArea, itemName] = parseInstancePath(path);

  // If itemName is not provided, is because it's a top level product
  if (!itemName && instance[product]?.properties?.selection) {
    instance[product].properties.selection = value;
    return;
  }

  // If the itemName is provided, update the selection
  if (instance[product]?.[displayArea]?.[itemName]?.selection) {
    instance[product][displayArea][itemName].selection = value;
    return;
  }
};

/**
 * Updates quantity in the instance object
 *
 * @param {object} instance The instance object to update
 * @param {string} productOrPath Product name for main product, or full path for addons
 * @param {number} value The new quantity value to set
 */
exports.updateSelection = updateSelection;
const updateQuantity = (instance, productOrPath, value) => {
  // Check if this is a path (addon) or just a product name (main product)
  if (productOrPath.includes('/')) {
    // Addon quantity update - use customData
    const [product, displayArea, itemName] = parseInstancePath(productOrPath);

    // Early return if required product or displayArea are missing
    if (!instance[product] || !instance[product][displayArea]) {
      return;
    }

    // Ensure the item object exists
    if (!instance[product][displayArea][itemName]) {
      instance[product][displayArea][itemName] = {};
    }

    // Set the quantity value in customData (as string, matching production format)
    instance[product][displayArea][itemName].customData = value.toString();
  } else {
    // Main product quantity update
    instance[productOrPath].properties.quantity = value;
  }
};

/**
 * Calculates the renewal details based on the price, term months, market, currency, and content
 *
 * @param {number} price The price of the renewal
 * @param {number} termMonths The number of months for the term
 * @param {string} market The market/locale for formatting the date
 * @param {string} currency The currency for formatting the price
 * @param {string} content The content placeholder for the renewal details
 * @returns {string} The renewal details with placeholders replaced by formatted date and price
*/
exports.updateQuantity = updateQuantity;
function getRenewalDetails(price, termMonths, market = 'en-US', currency, content) {
  const currentDate = new Date();
  const renewalDate = new Date(currentDate.setMonth(currentDate.getMonth() + termMonths));
  const options = {
    year: 'numeric',
    month: 'long'
  };
  const formattedDate = renewalDate.toLocaleDateString(market, options);
  const formattedPrice = formatMicrounits(price, market, currency);
  const renewalDetails = content?.replace('{formattedDate}', formattedDate)?.replace('{formattedPrice}', formattedPrice);
  return renewalDetails;
}

/* eslint-disable max-statements */
/**
 * Updates the cart with the provided instance.
 *
 * @param {string} postToCartUrl The URL to post the instance to.
 * @param {string} product The ID of the product.
 * @param {object} instance The instance to be added to the cart.
 * @param {URLSearchParams} parsedQueryParams The query parameters to append to the baseUrl.
 */
const updateCartWithInstance = async (postToCartUrl, product, instance, parsedQueryParams) => {
  // Define the parameters we want to extract and their conditions
  const paramConfig = [{
    name: 'itc'
  }, {
    name: 'hivemind'
  }, {
    name: 'contextState'
  }, {
    name: 'marketid'
  }, {
    name: 'currencyid'
  }, {
    name: 'tekcub',
    condition: params => params.get('src') === _appConfig.SOURCE_TYPES.SERP
  }];

  // Build URL with parameters
  const baseUrl = parsedQueryParams.get('src') === _appConfig.SOURCE_TYPES.SERP ? `${postToCartUrl}` : `${postToCartUrl}${product}`;
  const url = new URL(baseUrl);

  // Start with existing parameters from baseUrl
  const finalParams = new URLSearchParams(url.search);

  // Add parameters based on config
  paramConfig.forEach(({
    name,
    condition
  }) => {
    const value = parsedQueryParams.get(name);
    if (value && (!condition || condition(parsedQueryParams))) {
      finalParams.set(name, value);
    }
  });
  url.search = finalParams.toString();
  const response = await (0, _fetchWithHeader.fetchWithHeader)(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify(instance)
  }, 'SWP', _appConfig.TEKCUB_KEYS.PRECHECK_BFF_NODE);
  return response;
};

/**
 * Retrieves query parameters from the current URL and sets default values for missing parameters
 * @param {string[]} paramNames An array of parameter names to retrieve
 * @param {Object} [defaultValues={}] An optional object containing default values for the parameters
 * @param {boolean} [caseSensitive=false] An optional boolean to determine if the parameter names should be case sensitive
 * @returns {URLSearchParams} An object containing the retrieved query parameters
 */
exports.updateCartWithInstance = updateCartWithInstance;
const getAppParams = (paramNames = [], defaultValues = {}, caseSensitive = false) => {
  const params = new URLSearchParams();
  if (typeof window !== 'undefined') {
    const url = new URL(window.location.href);
    paramNames.forEach(paramName => {
      const name = caseSensitive ? paramName : paramName.toLowerCase();
      for (const [key, value] of url.searchParams.entries()) {
        const addParam = caseSensitive ? key === name : key.toLowerCase() === name;
        if (addParam && value != null) {
          params.set(paramName, value);
        }
      }
    });

    // set default values for missing parameters
    Object.entries(defaultValues).forEach(([key, value]) => {
      if (!params.has(key) && value != null) {
        params.set(key, value);
      }
    });
  }
  return params;
};

/**
 * Retrieves the quantity for a given product from the instance object
 *
 * @param {Object} instance The instance object containing the product data
 * @param {string} product The ID of the product
 * @returns {number} The quantity for the given product
 */
exports.getAppParams = getAppParams;
const getProductQuantity = (instance, product) => {
  return instance[product]?.properties?.quantity || 1;
};

/**
 * Retrieves the term for a given product from the instance object
 *
 * @param {Object} instance The instance object containing the product data
 * @param {string} product The ID of the product
 * @returns {Object} The term for the given product
 */
exports.getProductQuantity = getProductQuantity;
const getProductTerm = (instance, product) => {
  return instance[product]?.properties?.term || {};
};

/**
 * Extracts the app parameters from the URL search params
 *
 * @param {URLSearchParams} appParams The URL search params to extract the parameters from
 * @returns {Object} An object containing the extracted parameters and the query string
 */
exports.getProductTerm = getProductTerm;
const extractAppParams = appParams => {
  const entries = appParams.entries();
  const result = {};
  for (const [key, value] of entries) {
    result[key] = value;
  }
  return {
    ...result,
    queryString: appParams.toString()
  };
};
exports.extractAppParams = extractAppParams;
const getVisitorCookie = () => {
  const visitorId = (0, _base.getCookieValue)('visitor');
  const visitorCookie = `visitor=${visitorId}`;
  return visitorCookie;
};

/**
 * Calculates the total price based on the sale price, term length, quantity, market, and currency
 *
 * @param {number} salePrice The sale price of the product
 * @param {number} termLength The term length of the product
 * @param {number} quantity The quantity of the product
 * @param {string} market The market/locale for formatting the price
 * @param {string} currency The currency for formatting the price
 * @returns {string | undefined} The total price of the product
 */
exports.getVisitorCookie = getVisitorCookie;
const calculateTotalPrice = (salePrice, termLength, quantity, market, currency) => {
  if (!salePrice && salePrice !== 0) return;
  return formatMicrounits(salePrice * termLength * quantity, market, currency);
};

/**
 * Retrieves the price display based on the override, sale price, term type, content, per month override, and option price formatted
 *
 * @param {string} override The override for the price display
 * @param {string} salepriceformatted The sale price formatted
 * @param {string} termType The term type
 * @param {Object} content The content object
 * @param {string} perMonthOverride The per month override
 * @param {string} optionPriceFormatted The option price formatted
 * @returns {string} The price display
 */
exports.calculateTotalPrice = calculateTotalPrice;
const getPriceDisplay = (override, salepriceformatted, termType, content, perMonthOverride, optionPriceFormatted) => {
  let salePrice = salepriceformatted;
  if (override) return override;
  if (optionPriceFormatted) {
    salePrice = optionPriceFormatted;
  }
  return `${salePrice}${getTermPriceText(termType, content, perMonthOverride)}`;
};

/**
 * Retrieves the price description based on the total price, first description, and second description
 *
 * @param {string} totalPrice The total price
 * @param {string} firstDescription The first description
 * @param {string} secondDescription The second description
 * @returns {string} The price description
 */
exports.getPriceDisplay = getPriceDisplay;
const getPriceDescription = (totalPrice, firstDescription, secondDescription) => {
  if (!firstDescription && !secondDescription) return '';
  return firstDescription?.replace('{totalPrice}', totalPrice).trim() || secondDescription?.replace('{totalPrice}', totalPrice).trim();
};

/**
 * Retrieves the savings tag based on the savings and save percentage
 *
 * @param {number} savings The savings
 * @param {string} savePercentage The save percentage
 * @returns {string} The savings tag
 */
exports.getPriceDescription = getPriceDescription;
const getSavingsTag = (savings, savePercentage) => {
  if (!savings || savings === 0) return null;
  return savePercentage.replace('{value}', savings);
};

// Re-export domain utilities for consistency
exports.getSavingsTag = getSavingsTag;